import React, { useMemo } from "react";

import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
} from "@mui/material/styles";
import type { LinkProps, ThemeOptions } from "@mui/material";

import color from "color";

import LinkBehavior from "../../components/RouterLink";
import { useOverview } from "../../lib/hooks/useQueryBoard";
import { useIsFrontAssistDomain } from "../../lib/hooks/useDomain";

export const getOptimalTextColor = (
  textColor: string,
  bgColor: string,
  defaultColor: string,
): string => {
  const hexToRgb = (hex: string): [number, number, number] => {
    const cleanHex = hex.replace(/^#/, "");
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    return [r, g, b];
  };

  const getLuminance = ([r, g, b]: [number, number, number]): number => {
    const normalize = (c: number) =>
      c / 255 <= 0.03928 ? c / 255 / 12.92 : ((c / 255 + 0.055) / 1.055) ** 2.4;
    return (
      0.2126 * normalize(r) + 0.7152 * normalize(g) + 0.0722 * normalize(b)
    );
  };

  const getContrastRatio = (lum1: number, lum2: number): number => {
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
  };

  const textRgb = hexToRgb(textColor);
  const bgRgb = hexToRgb(bgColor);
  const textLuminance = getLuminance(textRgb);
  const bgLuminance = getLuminance(bgRgb);

  const contrastRatio = getContrastRatio(textLuminance, bgLuminance);

  if (contrastRatio >= 3.5) {
    return textColor;
  }

  return bgLuminance > 0.4 ? defaultColor : "#FFFFFF";
};

function createReceptionTheme(
  isFrontAssist: boolean,
  theme?: IDigitalReceptionTheme,
  fields?: OverviewField[],
): ThemeOptions {
  const primary =
    theme?.colors?.primary ?? (isFrontAssist ? "#33435d" : "#2D9CCA");

  const primaryLight = color(primary).lighten(0.55).hex();
  const primaryDark = color(primary).darken(0.25).hex();

  const background = theme?.colors?.background ?? "#FFFFFF";

  const textPrimary = theme?.colors?.textPrimary ?? "#212529";

  const contrastText = getOptimalTextColor(background, primary, textPrimary);

  return {
    typography: {
      fontFamily: ["DM Sans"].join(","),
      h1: {
        fontSize: "4.4rem",
        fontWeight: "700",
        color: textPrimary,
      },
      h2: {
        fontSize: "3.7rem",
        fontWeight: "700",
        color: textPrimary,
      },
      h3: {
        fontSize: "3.2rem",
        fontWeight: "700",
        color: textPrimary,
      },
      h4: {
        fontSize: "2.4rem",
        fontWeight: "700",
        color: textPrimary,
      },
    },
    palette: {
      primary: {
        main: primary,
        dark: primaryDark,
        light: primaryLight,
        contrastText,
      },
      secondary: {
        main: "#666666",
      },
      error: {
        main: "#e13c3c",
      },
      success: {
        main: "#05c00c",
      },
      text: {
        primary: textPrimary,
      },
      background: {
        default: background,
      },
    },
    components: {
      MuiFormControl: {
        styleOverrides: {
          root: {
            width: "100%",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: "3.2rem",
            color: textPrimary,
            background: color(background).lighten(0.1).hex(),
            border: `0.2rem solid ${textPrimary} !important`,

            "fieldset.MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
          input: {
            padding: "1.4rem 3.4rem !important",
          },
          inputAdornedStart: {
            marginLeft: "-2rem ",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "3.2rem",
            padding: "0 1rem",
            transform: "translate(1.4rem, 1.5rem) scale(1)",
            color: textPrimary,
          },
          shrink: {
            transform: "translate(2rem, -2.5rem) scale(0.75)",
            backgroundColor: color(background).lighten(0.1).hex(),
            padding: "0.4rem 2.5rem 0 1.5rem",
            borderRadius: "0.7rem",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: "2rem",
            paddingLeft: "1rem",
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            padding: "0",
          },
          endAdornment: {
            display: "none",
          },
          popper: {
            marginTop: "0.8rem !important",
            borderRadius: "1rem",
          },
          paper: {
            margin: "0",
            boxShadow: `0px 0px 4px -1px ${textPrimary}`,
            border: `1px solid ${textPrimary}`,
            borderRadius: "1rem",
            background: color(background).lighten(0.1).hex(),
          },
          listbox: {
            padding: "0 0 0.5rem",
            maxHeight: "21.8rem",
          },
          option: {
            fontSize: "3.2rem",
            padding: "1.2rem 3.2rem !important",
            color: textPrimary,
            minHeight: "auto !important",
            '&[aria-selected="true"]': {
              backgroundColor: color(background).lighten(0.08).hex(),
            },
          },
          noOptions: {
            fontSize: "3.2rem",
            padding: "1.2rem 2.8rem",
            color: color(textPrimary).lighten(0.2).hex(),
          },
          loading: {
            fontSize: "3.2rem",
            padding: "1.2rem 2.8rem",
            color: color(textPrimary).lighten(0.2).hex(),
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            fontSize: "3.2rem",
            textTransform: "none",
            borderRadius: "1rem",
            boxShadow: "none",
            color: contrastText,

            "&:hover": {
              boxShadow: "none",
            },
          },
          sizeMedium: {
            fontSize: "3.2rem",
            padding: "1.45rem 2rem",
          },
          outlined: {
            color: primary,
          },
        },
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            ".MuiButton-root:first-of-type": {
              borderBottomLeftRadius: 0,
            },
            ".MuiButton-root:last-of-type": {
              borderBottomRightRadius: 0,
            },
          },
        },
      },
      MuiLink: {
        defaultProps: {
          component: LinkBehavior,
        } as LinkProps,
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            marginTop: "-3rem!important",
            width: "56.5rem",
            height: "38rem",
            margin: "0",
            boxShadow: `0px 0px 4px -1px ${textPrimary}`,
            border: `1px solid ${textPrimary}`,
            background: color(background).lighten(0.1).hex(),

            li: {
              fontSize: "3.2rem",
              padding: "1.2rem 3.2rem !important",
              color: textPrimary,
              minHeight: "auto !important",
              img: {
                width: "4rem",
                marginRight: "3rem",
              },
              span: {
                fontSize: "inherit",
              },
              p: {
                fontSize: "inherit",
                fontWeight: "600",
              },
            },
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            button: {
              marginLeft: "1rem",
              paddingRight: "0",
              img: {
                width: "4rem",
              },
            },
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            marginTop: "3rem",
            width: "90%",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            width: `${100 / (fields?.length || 3)}%`,
            textAlign: "center",
            fontSize: "2.4rem",
            padding: "2rem 0",
          },
          head: {
            fontSize: "2.75rem",
            fontWeight: "700",
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            ".MuiInputBase-root": {
              display: "none",
            },
          },
          selectLabel: { display: "none" },
          displayedRows: { display: "none" },
          actions: {
            marginLeft: "0",
            svg: {
              width: "5rem",
              height: "5rem",
            },
          },
        },
      },
    },
  };
}

const ThemeProvider = (props: {
  children: React.ReactNode;
  theme?: IDigitalReceptionTheme;
  dataUpdatedAt?: number;
}) => {
  const { children, dataUpdatedAt, theme } = props;

  const { fields } = useOverview();

  const isFrontAssist = useIsFrontAssistDomain();

  const MUItheme = useMemo(() => {
    const options = createReceptionTheme(isFrontAssist, theme, fields);

    return createTheme(options);
  }, [theme, dataUpdatedAt, isFrontAssist]);

  return <MUIThemeProvider theme={MUItheme}>{children}</MUIThemeProvider>;
};

export { ThemeProvider };
